import React from 'react';
import { useHistory } from 'react-router-dom';
import { useGetUserProfileByIdQuery } from '../../redux/apis/endpoints/profileApi';
import { UserCircleIcon } from '@heroicons/react/24/solid';

const CustomerProfilePicture = ({ 
  customer, 
  fulfillmentGroups = [], 
  orderId,
  size = 'md', // 'sm', 'md', 'lg'
  onClick,
  className = '',
  showTooltip = true 
}) => {
  const history = useHistory();

  // Try to get customer ID from various sources
  const customerId = customer?.id || 
    (fulfillmentGroups && fulfillmentGroups.length > 0 ? fulfillmentGroups[0].customer?.id : null);

  // Use the new getUserProfileById endpoint for better profile picture retrieval
  const { data: userProfile, isLoading, isError } = useGetUserProfileByIdQuery(
    customerId,
    { skip: !customerId }
  );

  // Determine size classes
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10', 
    lg: 'h-12 w-12',
    xl: 'h-20 w-20'
  };

  const iconSizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12', 
    xl: 'h-16 w-16'
  };

  // Use profile data from the query, fallback to customer prop
  const profileData = userProfile || customer;
  
  // Properly extract profile picture URL from the profilePic collection field
  let profilePicUrl = null;
  if (profileData?.profilePic) {
    if (Array.isArray(profileData.profilePic) && profileData.profilePic.length > 0) {
      // profilePic is a collection, get the first image
      const firstImage = profileData.profilePic[0];
      profilePicUrl = firstImage?.formats?.thumbnail?.url || firstImage?.url;
    } else if (profileData.profilePic.url) {
      // Direct URL access
      profilePicUrl = profileData.profilePic.url;
    }
  }

  if (isLoading) {
    return (
      <div className={`${sizeClasses[size]} bg-gray-200 rounded-full animate-pulse ${className}`} />
    );
  }

  if (isError || !profileData) {
    return (
      <UserCircleIcon className={`${sizeClasses[size]} text-gray-400 ${className}`} />
    );
  }

  // Extract customer info for navigation and display
  const customerInfo = {
    id: profileData.id,
    name: profileData.username || profileData.firstname || profileData.email?.split('@')[0] || 'Customer',
    profilePicture: profilePicUrl,
    initials: getInitials(profileData)
  };

  // Get initials from customer data
  function getInitials(customer) {
    if (customer.firstname && customer.lastname) {
      return `${customer.firstname[0]}${customer.lastname[0]}`.toUpperCase();
    }
    if (customer.username) {
      const words = customer.username.split(' ');
      if (words.length > 1) {
        return `${words[0][0]}${words[1][0]}`.toUpperCase();
      }
      return customer.username.substring(0, 2).toUpperCase();
    }
    if (customer.firstname) {
      return customer.firstname.substring(0, 2).toUpperCase();
    }
    if (customer.email) {
      return customer.email.substring(0, 2).toUpperCase();
    }
    return 'U'; // Unknown user
  }

  // Handle click navigation
  const handleClick = () => {
    if (onClick) {
      onClick(profileData);
      return;
    }

    // Default navigation to messaging
    if (!profileData.id) return;
    
    // Use first fulfillment group for messaging context if available
    if (fulfillmentGroups && fulfillmentGroups.length > 0 && fulfillmentGroups[0].id) {
      history.push(`/message/fg/${fulfillmentGroups[0].id}`);
    } else {
      // Fallback to general messaging
      history.push('/message', {
        targetUserId: profileData.id,
        targetUserName: customerInfo.name,
        contextType: 'order',
        contextId: orderId
      });
    }
  };

  return (
    <div 
      className={`relative group cursor-pointer ${className}`}
      onClick={handleClick}
    >
      <div className={`${sizeClasses[size]} relative ${className}`}>
        {profilePicUrl ? (
          <img
            src={profilePicUrl}
            alt={`${profileData.username || profileData.firstname || 'Customer'}'s profile`}
            className={`${sizeClasses[size]} rounded-full object-cover border-2 border-gray-200`}
            onError={(e) => {
              // Fallback to initials or icon if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        
        {/* Fallback: Show initials or icon */}
        <div 
          className={`${sizeClasses[size]} rounded-full bg-brand-teal text-white flex items-center justify-center font-medium text-sm border-2 border-gray-200 ${profilePicUrl ? 'hidden' : 'flex'}`}
          style={{ display: profilePicUrl ? 'none' : 'flex' }}
        >
          {customerInfo.initials || <UserCircleIcon className={`${iconSizeClasses[size]} text-white`} />}
        </div>
      </div>
      
      {/* Tooltip */}
      {showTooltip && (
        <div className="
          absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
          bg-gray-900 text-white text-xs rounded py-1 px-2
          opacity-0 group-hover:opacity-100 transition-opacity duration-200
          pointer-events-none z-10
          whitespace-nowrap
        ">
          Message {customerInfo.name}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default CustomerProfilePicture; 